import TagManager from 'react-gtm-module'
import type { InvoicingDetails } from 'src/api/defs/InvoicingDetails.type'
import { getCookie } from 'src/utils/utils'

export const isMarketingCookieEnabled = (): boolean =>
    getCookie('marketing') === 'true'

export const buildGadsPayload = (): string =>
    JSON.stringify([getCookie('gclid'), getCookie('gad_campaignid')])

export const buildFbpixelPayload = (): string =>
    JSON.stringify([getCookie('fbclid'), getCookie('utm_campaign')])

export const buildMetaFbp = (): string | null =>
    isMarketingCookieEnabled() ? getCookie('_fbp') : null

export const buildMetaFbc = (): string | null => {
    if (!isMarketingCookieEnabled()) return null

    const fbc = getCookie('_fbc')
    if (fbc) return fbc

    const fbclid = new URLSearchParams(window.location.search).get('fbclid')
    return fbclid ? `fb.1.${Date.now()}.${fbclid}` : null
}

export const normalizeGaClientIdCookie = (
    gaCookie: string | null
): string | null => {
    if (!gaCookie) return null

    const parts = gaCookie.split('.')
    const isGaCookie =
        parts.length === 4 &&
        /^GA\d+$/.test(parts[0]) &&
        parts.slice(1).every(Boolean)

    return isGaCookie ? `${parts[2]}.${parts[3]}` : gaCookie
}

export const buildGaClientId = (): string | null =>
    normalizeGaClientIdCookie(getCookie('_ga'))

const normalizeText = (value?: string | null): string | undefined => {
    const normalized = value?.trim().replace(/\s+/g, ' ').toLowerCase()
    return normalized || undefined
}

const normalizePhoneNumber = (value?: string | null): string | undefined => {
    if (!value) return undefined

    const normalized = value.trim().replace(/[^\d+]/g, '')
    if (!normalized) return undefined

    return normalized.startsWith('00') ? `+${normalized.slice(2)}` : normalized
}

const sha256 = async (value: string): Promise<string> => {
    const encoded = new TextEncoder().encode(value)
    const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', encoded)
    const hashArray = Array.from(new Uint8Array(hashBuffer))

    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

const hashField = async (
    value?: string | null,
    normalizer: (value?: string | null) => string | undefined = normalizeText
): Promise<string | undefined> => {
    const normalized = normalizer(value)
    if (!normalized) return undefined

    return sha256(normalized)
}

export const buildUserDataSha256Payload = async (
    customer?: InvoicingDetails | null
): Promise<Record<string, string> | undefined> => {
    if (!customer || !isMarketingCookieEnabled()) return undefined

    if (!globalThis.crypto?.subtle) {
        console.warn(
            'Unable to hash GTM user data. crypto.subtle is not available'
        )
        return undefined
    }

    const addressLine = [customer.address?.street, customer.address?.number]
        .filter(Boolean)
        .join(' ')

    const [
        email,
        phone,
        firstName,
        lastName,
        address,
        city,
        postcode,
        country
    ] = await Promise.all([
        hashField(customer.email),
        hashField(customer.telephoneNumber, normalizePhoneNumber),
        hashField(customer.firstName),
        hashField(customer.lastName),
        hashField(addressLine),
        hashField(customer.address?.city),
        hashField(customer.address?.zip),
        hashField(customer.address?.countryCode)
    ])

    const entries = [
        ['email', email],
        ['phone', phone],
        ['first_name', firstName],
        ['last_name', lastName],
        ['address', address],
        ['city', city],
        ['postcode', postcode],
        ['country', country]
    ] as const

    const payload = Object.fromEntries(
        entries.filter(([, value]) => Boolean(value))
    ) as Record<string, string>

    return Object.keys(payload).length > 0 ? payload : undefined
}

export function gtmEvent(event: string, params: Record<string, unknown> = {}) {
    TagManager.dataLayer({
        dataLayer: {
            event,
            ...params
        }
    })
}

export function gtmMarketingEvent(
    event: string,
    params: Record<string, unknown> = {}
) {
    if (!isMarketingCookieEnabled()) return
    gtmEvent(event, params)
}
